import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SemanticState, SemanticEvent, Understanding, KnowledgeGraph, TimeframeData, ConsensusState } from '@/types/semantic.types';

interface SemanticSliceState {
  currentState: SemanticState | null;
  knowledgeGraph: KnowledgeGraph | null;
  timeframes: TimeframeData[];
  consensus: ConsensusState | null;
  recentEvents: SemanticEvent[];
  activeUnderstandings: Understanding[];
  loading: boolean;
  error: string | null;
  isConnected: boolean;
}

const initialState: SemanticSliceState = {
  currentState: null,
  knowledgeGraph: null,
  timeframes: [],
  consensus: null,
  recentEvents: [],
  activeUnderstandings: [],
  loading: false,
  error: null,
  isConnected: false,
};

const semanticSlice = createSlice({
  name: 'semantic',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setSemanticState: (state, action: PayloadAction<SemanticState>) => {
      state.currentState = action.payload;
      state.activeUnderstandings = action.payload.activeInterpretations;
    },
    setKnowledgeGraph: (state, action: PayloadAction<KnowledgeGraph>) => {
      state.knowledgeGraph = action.payload;
    },
    setTimeframes: (state, action: PayloadAction<TimeframeData[]>) => {
      state.timeframes = action.payload;
    },
    setConsensus: (state, action: PayloadAction<ConsensusState>) => {
      state.consensus = action.payload;
    },
    addSemanticEvent: (state, action: PayloadAction<SemanticEvent>) => {
      state.recentEvents.unshift(action.payload);
      if (state.recentEvents.length > 100) {
        state.recentEvents.pop();
      }
    },
    clearEvents: (state) => {
      state.recentEvents = [];
    },
    updateTimeframe: (state, action: PayloadAction<TimeframeData>) => {
      const index = state.timeframes.findIndex(tf => tf.name === action.payload.name);
      if (index !== -1) {
        state.timeframes[index] = action.payload;
      } else {
        state.timeframes.push(action.payload);
      }
    },
    setConnected: (state, action: PayloadAction<boolean>) => {
      state.isConnected = action.payload;
    },
  },
});

export const {
  setLoading,
  setError,
  setSemanticState,
  setKnowledgeGraph,
  setTimeframes,
  setConsensus,
  addSemanticEvent,
  clearEvents,
  updateTimeframe,
  setConnected,
} = semanticSlice.actions;

export default semanticSlice.reducer;
